import { useMemo, useState, useEffect, FC } from 'react';
import { Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import {
    Box,
    Button,
    Center,
    Flex,
    Icon,
    IconButton,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import { IList, useGetListsQuery, useDeleteListMutation, useLazyGetListDataQuery } from 'core/API/listApi';

import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { ChevronDownIcon, DeleteIcon } from '@chakra-ui/icons';
import { LocaleKey, useTranslation } from 'helpers/languages/translations';
import { downloadExcelFile } from 'utils/files';
import { DITable } from 'plugins/DITable';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import TablePagination from 'plugins/DITable/Pagination';
import { iconTableStyle, tableToolbarStyle } from 'shared/styles';
import icons from 'shared/icons';
import { checkRequestStatus } from 'utils/erros';

import { LIST_FIELDS, HIDDEN_COLUMN, PREFERED_COLUMN_ORDER } from '../../../const';
import { KeyOflistType, listTypes } from '../ListTemplates/helpers/HeadersList';

const initialState = {
    hiddenColumns: [...HIDDEN_COLUMN],
};

const MListsViewer: FC<{
    toolId: number;
    readonly?: boolean;
}> = ({ toolId, readonly }) => {
    const { data: lists, isError, isLoading } = useGetListsQuery({ idTool: toolId });
    const [listData] = useLazyGetListDataQuery();
    const [deleteListData, { isLoading: isDeleteListLoading }] = useDeleteListMutation();
    const companyInfo = useGetCompanyInfoSelector();
    const [dashboardListsData, setListsData] = useState<IList[]>([]);
    const navigate = useNavigate();
    const translate = useTranslation();
    const toast = useToast();

    useEffect(() => {
        if (lists && !isLoading && !isError) {
            setListsData(lists.lists.filter((list) => listTypes[toolId as KeyOflistType].includes(list.type)));
        }
    }, [isError, isLoading, lists]);

    const filtreAndOrderColumns = (tableHeader: string[]) => {
        const authorizedSort = [...PREFERED_COLUMN_ORDER];
        let headerArray = [...tableHeader];

        headerArray = authorizedSort
            .filter((elem) => headerArray.includes(elem))
            .concat(headerArray.filter((elem: string) => !authorizedSort.includes(elem)).sort());

        return headerArray;
    };

    const deleteCallback = async (id: number) => {
        try {
            toast({
                title: translate(`list.delete.message`),
                status: 'info',
                isClosable: true,
            });

            const res = await deleteListData({ idTool: toolId, idList: id });

            checkRequestStatus(res, translate(`list.edit.error`) as string);

            toast({
                title: translate(`list.edit.success`),
                status: 'success',
                isClosable: true,
            });
        } catch {
            toast({
                title: translate(`list.edit.error`),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const getTypeTranslation = (type: string) => {
        const translatedType = translate(`list.type.${type}` as LocaleKey);
        return translatedType !== 'keyNotFound' ? translatedType : type;
    };

    const handleDownloadListData = async (id: number) => {
        const res = await listData({ idTool: toolId, idList: id });

        try {
            if (res.data?.lists) {
                const list = res.data.lists[0];
                const data = [list.columns, ...list.data];
                downloadExcelFile(list.name, 'Lists', data as string[][]);
            }
        } catch {
            toast({
                title: "Can't download error file",
                status: 'error',
                isClosable: true,
            });
        }
    };

    const columns: Readonly<Column<any>[]> = useMemo(() => {
        if (!isLoading && !isError && lists) {
            const filteredTableHeader: Column<any>[] = filtreAndOrderColumns(LIST_FIELDS)
                .filter((cName) => {
                    if (companyInfo?.country_id !== 840 && companyInfo?.country_id !== 124 && cName === 'upc_type') {
                        return false;
                    }
                    return true;
                })
                .map((header: string) => {
                    switch (header) {
                        case 'name':
                            return {
                                Header: translate(`list.column.name`) as string,
                                accessor: header,
                                Cell: ({ row: { values, original } }) => {
                                    return (
                                        <Flex>
                                            <Link
                                                onClick={() => {
                                                    navigate('./list', {
                                                        state: {
                                                            toolId,
                                                            listId: original.id,
                                                        },
                                                    });
                                                }}
                                                color="blue.500"
                                            >
                                                {values.name}
                                            </Link>
                                        </Flex>
                                    );
                                },
                            };
                        case 'actions':
                            return {
                                Header: translate(`list.column.actions`) as string,
                                accessor: header,
                                Cell: ({ row: { values, original } }) => {
                                    const DownloadIcon = icons.download;
                                    return (
                                        <Flex>
                                            <Menu size="xs">
                                                <MenuButton
                                                    size="xs"
                                                    as={Button}
                                                    rightIcon={<ChevronDownIcon />}
                                                    isDisabled={readonly}
                                                >
                                                    {translate(`list.menu.button.actions`)}
                                                </MenuButton>
                                                <MenuList>
                                                    <MenuItem
                                                        onClick={async () => await deleteCallback(original.id)}
                                                        icon={<DeleteIcon />}
                                                    >
                                                        {translate(`list.menu.button.delete`)}
                                                    </MenuItem>
                                                    {/* This comment is temporary  */}
                                                    {/* <MenuItem
                                                        onClick={async () => handleDownloadListData(original.id)}
                                                        icon={<DownloadIcon />}
                                                    >
                                                        Download
                                                    </MenuItem> */}
                                                </MenuList>
                                            </Menu>
                                        </Flex>
                                    );
                                },
                            };
                        case 'type':
                            return {
                                Header: translate(`list.column.type`) as string,
                                accessor: header,
                                Cell: ({ row: { values, original } }) => {
                                    return <Flex>{getTypeTranslation(values.type as string)}</Flex>;
                                },
                            };
                        default:
                            return {
                                Header: translate('list.column.upc_type') as string,
                                accessor: header,
                                minWidth: 200,
                                width: 200,
                                Cell: ({ row: { values, original } }) => {
                                    return <Flex>{values.upc_type === '0' ? 'Default' : values.upc_type}</Flex>;
                                },
                            };
                    }
                });

            return filteredTableHeader;
        }

        return [];
    }, [isLoading, isError]);

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const tableInstance = useTable(
        { data: dashboardListsData, columns, defaultColumn, initialState },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const renderTableToolbar = () => {
        const { state, setGlobalFilter } = tableInstance;
        const PlusIcon = icons.plus;

        return (
            <Flex {...tableToolbarStyle}>
                <GlobalFilter globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} />
                <IconButton
                    {...iconTableStyle}
                    onClick={() => {
                        navigate('./list', {
                            state: {
                                toolId,
                                listId: 0,
                            },
                        });
                    }}
                    aria-label="add list"
                    size="sm"
                    icon={<Icon as={PlusIcon} />}
                    isDisabled={readonly}
                />
            </Flex>
        );
    };

    return (
        <Box mt="16px">
            {renderTableToolbar()}
            {columns.length !== 0 && <DITable {...tableInstance} isPagination />}
            {isLoading ? (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            ) : (
                <TablePagination tableInstance={tableInstance} />
            )}
        </Box>
    );
};

export default MListsViewer;
